import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
    ArrowLeft, ShieldCheck, ScrollText, CreditCard, Scan, FileText,
    AlertTriangle, CheckCircle2, ExternalLink, Clock, Mail, Send, Printer,
} from "lucide-react";

/**
 * /compliance-cockpit — one-URL launch-readiness overview.
 *
 * Aggregates data from 5 existing admin endpoints and renders a single
 * hub: Paddle readiness · Launch readiness · Legal CMS status ·
 * last prohibited-data scan · quick links to the 4 human-professional
 * briefs.
 */
export default function ComplianceCockpit() {
    const [state, setState] = useState({ loading: true });
    const [digest, setDigest] = useState({ sending: false, message: null, tone: null, lastRun: null });

    useEffect(() => {
        (async () => {
            const safe = async (url, fallback) => {
                try { return (await api.get(url)).data; } catch { return fallback; }
            };
            const [paddleR, launchR, legalR, scanR, digestR] = await Promise.all([
                safe("/admin/paddle/readiness", { items: [], blockers: 0, mock_mode: true, environment: "sandbox" }),
                safe("/admin/compliance/readiness", { categories: [], blockers: 0, overall_ready: false }),
                safe("/admin/legal-docs", { docs: [] }),
                safe("/admin/compliance/prohibited-scan?hours=168", { findings_total: 0, scanned_counts: {}, generated_at: null }),
                safe("/admin/compliance/digest/history?limit=1", { runs: [] }),
            ]);
            setState({ loading: false, paddleR, launchR, legalR, scanR });
            setDigest((d) => ({ ...d, lastRun: (digestR.runs || [])[0] || null }));
        })();
    }, []);

    async function sendTestDigest() {
        setDigest((d) => ({ ...d, sending: true, message: null, tone: null }));
        try {
            const { data } = await api.post("/admin/compliance/digest/send");
            const sent = data?.delivery?.sent;
            const reason = data?.delivery?.reason;
            const recipients = (data?.recipients || []).join(", ") || "no recipients resolved";
            const message = sent
                ? `Digest emailed to ${recipients}.`
                : `Digest compiled but not delivered (${reason || "unknown"}). Recipients: ${recipients}.`;
            setDigest({ sending: false, message, tone: sent ? "ok" : "warn", lastRun: data });
        } catch (e) {
            setDigest((d) => ({
                ...d, sending: false,
                message: e?.response?.data?.detail || "Failed to send digest.",
                tone: "warn",
            }));
        }
    }

    const previewUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/compliance/digest/preview`;
    const printUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/compliance/digest/print`;

    if (state.loading) return <div className="p-8 text-sm text-stone-500">Loading cockpit…</div>;

    const { paddleR, launchR, legalR, scanR } = state;
    const paddleReady = paddleR.items.filter((i) => i.status === "Ready").length;
    const paddleTotal = paddleR.items.length || 14;
    const paddleBlockers = paddleR.blockers || 0;
    const paddleScore = paddleTotal ? Math.round((paddleReady / paddleTotal) * 100) : 0;

    const launchReady = launchR.categories.filter((c) => c.status === "Ready" || c.status === "Not Applicable").length;
    const launchTotal = launchR.categories.length || 17;
    const launchBlockers = launchR.blockers || 0;

    const legalByStatus = legalR.docs.reduce((acc, d) => {
        acc[d.status] = (acc[d.status] || 0) + 1; return acc;
    }, {});
    const legalPublished = legalByStatus["Published"] || 0;
    const legalDraft = (legalByStatus["Draft"] || 0) + (legalByStatus["Legal Review Required"] || 0);

    const scanClean = (scanR.findings_total || 0) === 0;

    const overallReady = paddleBlockers === 0 && launchBlockers === 0 && paddleR.payment_ready && launchR.overall_ready;

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="compliance-cockpit">
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/dashboard" className="text-stone-500 hover:text-stone-900 flex items-center gap-1"><ArrowLeft size={12} /> Dashboard</Link>
                <span className="text-stone-300">/</span>
                <span className="font-medium">Trust &amp; Compliance Cockpit</span>
            </div>

            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Trust &amp; Compliance Cockpit</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        One page. Every launch-readiness signal VAULT tracks. Nothing here auto-declares
                        compliance — it&apos;s an honest status board for you and your advisors.
                    </p>
                </div>
                <div
                    data-testid="cockpit-overall"
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
                        overallReady ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                     : "bg-amber-50 text-amber-800 border border-amber-200"
                    }`}
                >
                    {overallReady ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                    {overallReady ? "Ready for launch review" : "Not launch-ready — see blockers below"}
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cockpit-kpis">
                <Kpi icon={CreditCard} title="Paddle readiness" value={`${paddleReady} / ${paddleTotal}`}
                    caption={paddleR.mock_mode ? "Sandbox · MOCK MODE" : `Env: ${paddleR.environment}`}
                    tone={paddleBlockers ? "warn" : paddleReady === paddleTotal ? "ok" : "muted"}
                    linkTo="/admin/paddle-readiness" testid="kpi-paddle" />
                <Kpi icon={ShieldCheck} title="Launch categories ready" value={`${launchReady} / ${launchTotal}`}
                    caption={`${launchBlockers} blocker${launchBlockers === 1 ? "" : "s"}`}
                    tone={launchBlockers ? "warn" : launchReady === launchTotal ? "ok" : "muted"}
                    linkTo="/compliance-ops" testid="kpi-launch" />
                <Kpi icon={FileText} title="Legal docs published" value={`${legalPublished}`}
                    caption={`${legalDraft} in draft / review`}
                    tone={legalPublished > 0 ? "ok" : "warn"}
                    linkTo="/compliance-ops" testid="kpi-legal" />
                <Kpi icon={Scan} title="Prohibited-data scan" value={scanClean ? "Clean" : `${scanR.findings_total} hits`}
                    caption={scanR.generated_at ? `Ran ${scanR.generated_at.slice(0, 10)}` : "Never run"}
                    tone={scanClean ? "ok" : "warn"} linkTo="/compliance-ops" testid="kpi-scan" />
            </div>

            {/* Blockers surfaced */}
            {(paddleBlockers > 0 || launchBlockers > 0) && (
                <section className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-5" data-testid="cockpit-blockers">
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-2"><AlertTriangle size={14} /> Blockers</div>
                    <ul className="text-sm space-y-1.5">
                        {paddleR.items.filter((i) => i.status === "Blocked").map((i) => (
                            <li key={`p-${i.item}`}>· <b>Paddle:</b> {i.item}</li>
                        ))}
                        {launchR.categories.filter((c) => c.status === "Blocker").map((c) => (
                            <li key={`l-${c.category}`}>· <b>Launch:</b> {c.category}</li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Legal CMS split */}
            <section className="mt-6 rounded-xl border border-stone-200 bg-white p-5">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-3">Legal CMS status</div>
                {legalR.docs.length === 0 ? (
                    <div className="text-sm text-stone-400">
                        No documents recorded in the Legal CMS yet — public pages currently render static copy from{" "}
                        <code className="text-xs">PublicPage.jsx</code>. Migrate a document by creating it under{" "}
                        <code className="text-xs">POST /api/admin/legal-docs</code> then submit → approve → publish.
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-2">
                        {legalR.docs.map((d) => (
                            <div key={d.id} className="flex items-center justify-between rounded-md border border-stone-100 px-3 py-2 text-sm">
                                <span className="font-mono text-xs">{d.slug}</span>
                                <StatusPill s={d.status} />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Weekly digest controls */}
            <section
                className="mt-6 rounded-xl border border-stone-200 bg-white p-5"
                data-testid="cockpit-digest"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-1 flex items-center gap-2">
                            <Mail size={12} /> Weekly compliance digest
                        </div>
                        <div className="text-sm text-stone-600 max-w-xl">
                            Every Monday at 09:00 IST, an email with this snapshot is delivered to the
                            admin (via <code className="text-xs">RESEND_API_KEY</code>). Override recipients
                            with the <code className="text-xs">DIGEST_RECIPIENTS</code> env var
                            (comma-separated). Nothing here is auto-declared &mdash; it&apos;s a status board.
                        </div>
                        {digest.lastRun?.started_at && (
                            <div className="text-xs text-stone-500 mt-2 flex items-center gap-1.5">
                                <Clock size={11} /> Last run: {String(digest.lastRun.started_at).slice(0, 16).replace("T", " ")} UTC
                                {digest.lastRun?.delivery?.sent ? " · delivered" : " · not delivered"}
                            </div>
                        )}
                        {digest.message && (
                            <div
                                data-testid="digest-status-msg"
                                className={`mt-3 text-xs rounded-md px-3 py-2 border inline-flex items-center gap-1.5 ${
                                    digest.tone === "ok"
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                        : "border-amber-200 bg-amber-50 text-amber-800"
                                }`}
                            >
                                {digest.tone === "ok"
                                    ? <CheckCircle2 size={12} />
                                    : <AlertTriangle size={12} />} {digest.message}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                        <button
                            onClick={sendTestDigest}
                            disabled={digest.sending}
                            data-testid="digest-send-test-btn"
                            className="inline-flex items-center gap-2 rounded-lg bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-50"
                        >
                            <Send size={13} />
                            {digest.sending ? "Sending…" : "Send test digest now"}
                        </button>
                        <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="digest-preview-link"
                            className="inline-flex items-center gap-1.5 justify-center rounded-lg border border-stone-200 px-4 py-2 text-xs font-semibold text-stone-700 hover:border-stone-400"
                        >
                            Preview HTML <ExternalLink size={11} />
                        </a>
                        <a
                            href={printUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="digest-print-pdf-link"
                            className="inline-flex items-center gap-1.5 justify-center rounded-lg border border-stone-200 px-4 py-2 text-xs font-semibold text-stone-700 hover:border-stone-400"
                            title="Opens a print-ready page and triggers the browser's Print → Save as PDF dialog"
                        >
                            <Printer size={11} /> Save as PDF
                        </a>
                    </div>
                </div>
            </section>

            {/* Human-professional briefs */}
            <section className="mt-6" data-testid="cockpit-briefs">
                <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-3">Human sign-offs required</div>
                <div className="grid md:grid-cols-2 gap-4">
                    <BriefCard
                        title="CA Tax Review" href="/app/docs/CA_TAX_REVIEW_BRIEF.md"
                        summary="GST on MoR SaaS · LUT · FIRC/Softex · DTAA + Form 10F · TDS. 10 questions ready to send."
                        icon={ScrollText} testid="brief-ca"
                    />
                    <BriefCard
                        title="Legal Counsel Review" href="/app/docs/LEGAL_COUNSEL_REVIEW_BRIEF.md"
                        summary="MoR phrasing · dark-pattern check · DPDP Grievance Officer · endorsement disclosure. 17 documents prioritised."
                        icon={ScrollText} testid="brief-legal"
                    />
                    <BriefCard
                        title="Paddle Sandbox Runbook" href="/app/docs/PADDLE_SANDBOX_RUNBOOK.md"
                        summary="9 steps · paste 4 keys · register webhook · map plans · run the 25-test verification script."
                        icon={CreditCard} testid="brief-paddle"
                    />
                    <BriefCard
                        title="Verification Script" href="/app/scripts/paddle_sandbox_verify.py"
                        summary="Runs the full 25-test lifecycle. Prints ✓/✗ per test + final N passed / M failed."
                        icon={Clock} testid="brief-script"
                    />
                </div>
            </section>
        </div>
    );
}

function Kpi({ icon: Icon, title, value, caption, tone, linkTo, testid }) {
    const toneCls = tone === "ok" ? "border-emerald-200 bg-emerald-50/40"
                    : tone === "warn" ? "border-amber-200 bg-amber-50/40"
                    : "border-stone-200 bg-white";
    const inner = (
        <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testid}>
            <div className="flex items-center justify-between text-stone-500">
                <Icon size={14} />
                {linkTo && <ExternalLink size={11} />}
            </div>
            <div className="mt-2 text-[11px] font-semibold tracking-wider uppercase text-stone-500">{title}</div>
            <div className="mt-1 text-2xl font-bold tracking-tight">{value}</div>
            <div className="mt-1 text-xs text-stone-500">{caption}</div>
        </div>
    );
    return linkTo ? <Link to={linkTo}>{inner}</Link> : inner;
}

function StatusPill({ s }) {
    const map = {
        "Published": "bg-emerald-50 text-emerald-700",
        "Approved": "bg-blue-50 text-blue-700",
        "Legal Review Required": "bg-amber-50 text-amber-800",
        "Draft": "bg-stone-100 text-stone-600",
        "Replaced": "bg-stone-100 text-stone-400 line-through",
        "Archived": "bg-stone-100 text-stone-400",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${map[s] || "bg-stone-100"}`}>{s}</span>;
}

function BriefCard({ title, href, summary, icon: Icon, testid }) {
    return (
        <a
            href={href} target="_blank" rel="noopener noreferrer"
            data-testid={testid}
            className="block rounded-xl border border-stone-200 bg-white p-4 hover:border-amber-400 transition"
        >
            <div className="flex items-center gap-2 text-sm font-semibold"><Icon size={13} /> {title} <ExternalLink size={11} className="text-stone-400" /></div>
            <div className="text-xs text-stone-500 mt-1.5 leading-relaxed">{summary}</div>
        </a>
    );
}

